import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import api from '../services/api';
import { API_ENDPOINTS } from '../utils/constants';
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiFilter } from 'react-icons/fi';
import SlideShow from '../components/SlideShow';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingHorizontal, setLoadingHorizontal] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [error, setError] = useState(null);
  
  const scrollContainerRef = useRef(null);
  const categoryScrollRef = useRef(null);
  const mobileCategoryScrollRef = useRef(null);
  const observerRef = useRef(null);
  const lastProductRef = useRef(null);
  
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canScrollCategoryLeft, setCanScrollCategoryLeft] = useState(false);
  const [canScrollCategoryRight, setCanScrollCategoryRight] = useState(true);
  const [canScrollMobileCategoryLeft, setCanScrollMobileCategoryLeft] = useState(false);
  const [canScrollMobileCategoryRight, setCanScrollMobileCategoryRight] = useState(true);
  
  const [autoScrollInterval, setAutoScrollInterval] = useState(null);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  useEffect(() => {
    fetchCategories();
    fetchFeaturedProducts();
    fetchAllProducts(1);
  }, []);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [selectedCategory]);

  useEffect(() => {
    if (allProducts.length > 0) {
      startAutoScroll();
    }

    return () => {
      if (autoScrollInterval) {
        clearInterval(autoScrollInterval);
      }
    };
  }, [allProducts]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (loadingMore) return;
    
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        loadMoreProducts();
      }
    }, options);

    if (lastProductRef.current) {
      observer.observe(lastProductRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [allProducts, hasMore, loadingMore]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get(API_ENDPOINTS.CATEGORIES);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      const params = { limit: 10, featured: true };
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }
      const response = await api.get(API_ENDPOINTS.PRODUCTS, { params });
      setFeaturedProducts(response.data);
    } catch (err) {
      setError('មិនអាចផ្ទុកទំនិញបានទេ');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async (pageNum, reset = true) => {
    try {
      if (reset) {
        setLoadingHorizontal(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await api.get(API_ENDPOINTS.PRODUCTS, {
        params: { 
          limit: 2000,
          page: pageNum
        }
      });
      
      const newProducts = response.data.data || response.data;
      const total = response.data.total || newProducts.length;
      
      setTotalProducts(total);
      
      if (reset) {
        setAllProducts(newProducts);
        setPage(1);
        setHasMore(newProducts.length === 20);
      } else {
        setAllProducts(prev => [...prev, ...newProducts]);
        setHasMore(newProducts.length === 20);
      }
      
    } catch (err) {
      console.error('Error fetching all products:', err);
      if (reset) {
        setError('មិនអាចផ្ទុកផលិតផលបានទេ');
      }
    } finally {
      if (reset) {
        setLoadingHorizontal(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const loadMoreProducts = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    
    try {
      setLoadingMore(true);
      const response = await api.get(API_ENDPOINTS.PRODUCTS, {
        params: { 
          limit: 20,
          page: nextPage
        }
      });
      
      const newProducts = response.data.data || response.data;
      const total = response.data.total || newProducts.length;
      
      setTotalProducts(total);
      setAllProducts(prev => [...prev, ...newProducts]);
      setHasMore(newProducts.length === 20);
      
    } catch (err) {
      console.error('Error loading more products:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const checkCategoryScrollButtons = () => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
      setCanScrollCategoryLeft(scrollLeft > 0);
      setCanScrollCategoryRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const checkMobileCategoryScrollButtons = () => {
    if (mobileCategoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = mobileCategoryScrollRef.current;
      setCanScrollMobileCategoryLeft(scrollLeft > 0);
      setCanScrollMobileCategoryRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkScrollButtons, 300);
    }
  };

  const scrollCategory = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = categoryScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      categoryScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkCategoryScrollButtons, 300);
    }
  };

  const scrollMobileCategory = (direction) => {
    if (mobileCategoryScrollRef.current) {
      const scrollAmount = 200;
      const newScrollLeft = mobileCategoryScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      mobileCategoryScrollRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkMobileCategoryScrollButtons, 300);
    }
  };

  const startAutoScroll = () => {
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
    }

    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          scrollContainerRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollContainerRef.current.scrollTo({ 
            left: scrollLeft + 300, 
            behavior: 'smooth' 
          });
        }
        
        setTimeout(checkScrollButtons, 300);
      }
    }, 3000);

    setAutoScrollInterval(interval);
  };

  const stopAutoScroll = () => {
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
      setAutoScrollInterval(null);
    }
  };

  const resumeAutoScroll = () => {
    if (!autoScrollInterval) {
      startAutoScroll();
    }
  };

  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Slideshow */}
      <SlideShow />

      {/* Horizontal Auto-Scroll Products Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">
              ផលិតផល<span className="text-black">ថ្មីៗ</span>
            </h2>
            <div className="flex items-center gap-2">
              {/* Scroll Buttons */}
              <button
                onClick={() => scroll('left')}
                onMouseEnter={stopAutoScroll}
                onMouseLeave={resumeAutoScroll}
                disabled={!canScrollLeft}
                className={`p-2 border ${
                  canScrollLeft 
                    ? 'border-gray-300 text-gray-700 hover:bg-black hover:text-white hover:border-black' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
              >
                <FiChevronLeft size={20} />
              </button>
              <button
                onClick={() => scroll('right')}
                onMouseEnter={stopAutoScroll}
                onMouseLeave={resumeAutoScroll}
                disabled={!canScrollRight}
                className={`p-2 border ${
                  canScrollRight 
                    ? 'border-gray-300 text-gray-700 hover:bg-black hover:text-white hover:border-black' 
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
              >
                <FiChevronRight size={20} />
              </button>
              <Link 
                to="/products" 
                className="flex items-center gap-1 text-black hover:text-gray-700 font-medium text-sm ml-2"
              >
                <span>មើលទាំងអស់</span>
                <FiArrowRight />
              </Link>
            </div>
          </div>

          {/* Horizontal Scroll Container */}
          {loadingHorizontal ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <div 
              className="relative"
              onMouseEnter={stopAutoScroll}
              onMouseLeave={resumeAutoScroll}
            >
              <div 
                ref={scrollContainerRef}
                className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onScroll={checkScrollButtons}
              >
                {allProducts.slice(0, 20).map((product) => (
                  <div key={product.id} className="flex-none w-48 md:w-56">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>

              {/* Left Gradient Fade */}
              {canScrollLeft && (
                <div className="absolute left-0 top-0 bottom-4 w-12 bg-gradient-to-r from-white to-transparent pointer-events-none" />
              )}

              {/* Right Gradient Fade */}
              {canScrollRight && (
                <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none" />
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products Section with Category Filter */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">
              ផលិតផល<span className="text-black">ទាំងអស់</span>
            </h2>
            <Link 
              to="/products" 
              className="flex items-center gap-1 text-black hover:text-gray-700 font-medium text-sm"
            >
              <span>មើលទាំងអស់</span>
              <FiArrowRight />
            </Link>
          </div>

          {/* Desktop Category Scroll - Hidden on Mobile */}
          <div className="hidden md:block mb-6 relative">
            <div className="flex items-center">
              {canScrollCategoryLeft && (
                <button 
                  onClick={() => scrollCategory('left')}
                  className="absolute left-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-gray-100 hover:text-black"
                >
                  <FiChevronLeft size={18} />
                </button>
              )}
              
              <div 
                ref={categoryScrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide px-8"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onScroll={checkCategoryScrollButtons}
              >
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors ${
                    selectedCategory === '' 
                      ? 'bg-black text-white border-black' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-black hover:border-black'
                  }`}
                >
                  ទាំងអស់
                </button>
                
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors ${
                      selectedCategory === category.id 
                        ? 'bg-black text-white border-black' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-black hover:border-black'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {canScrollCategoryRight && (
                <button 
                  onClick={() => scrollCategory('right')}
                  className="absolute right-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-gray-100 hover:text-black"
                >
                  <FiChevronRight size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Category Scroll - All Categories with Right to Left Scroll */}
          <div className="md:hidden mb-6">
            <div className="flex items-center relative">
              {canScrollMobileCategoryLeft && (
                <button 
                  onClick={() => scrollMobileCategory('left')}
                  className="absolute left-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-gray-100 hover:text-black"
                >
                  <FiChevronLeft size={18} />
                </button>
              )}
              
              <div 
                ref={mobileCategoryScrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide px-8 py-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onScroll={checkMobileCategoryScrollButtons}
              >
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors ${
                    selectedCategory === '' 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-black'
                  }`}
                >
                  ទាំងអស់
                </button>
                
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex-none px-4 py-2 text-sm border whitespace-nowrap transition-colors ${
                      selectedCategory === category.id 
                        ? 'bg-black text-white border-black' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-black'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {canScrollMobileCategoryRight && (
                <button 
                  onClick={() => scrollMobileCategory('right')}
                  className="absolute right-0 z-10 w-8 h-8 bg-white border border-gray-200 flex items-center justify-center shadow-sm hover:bg-gray-100 hover:text-black"
                >
                  <FiChevronRight size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Active Category Indicator */}
          {selectedCategory && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-gray-500">កំពុងត្រងតាម:</span>
              <span className="px-3 py-1 bg-gray-100 border border-gray-200 text-black text-sm flex items-center gap-2">
                {categories.find(c => c.id === selectedCategory)?.name}
                <button 
                  onClick={() => setSelectedCategory('')}
                  className="hover:text-gray-700 font-bold"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          {/* Products Grid with Infinite Scroll */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {featuredProducts.length === 0 ? (
                <div className="text-center py-12 bg-white border border-gray-200">
                  <p className="text-gray-500 mb-4">មិនមានទំនិញក្នុងប្រភេទនេះទេ</p>
                  <button
                    onClick={() => setSelectedCategory('')}
                    className="bg-black text-white px-6 py-2.5 text-sm hover:bg-gray-800 transition-colors"
                  >
                    មើលទាំងអស់
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {featuredProducts.map((product, index) => (
                      <div 
                        key={product.id}
                        ref={index === featuredProducts.length - 1 ? lastProductRef : null}
                      >
                        <ProductCard product={product} />
                      </div>
                    ))}
                  </div>
                  
                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  )}
                  
                  {/* No More Products Message */}
                  {!hasMore && featuredProducts.length > 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      អ្នកបានមើលផលិតផលទាំងអស់ហើយ
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Mobile View All Button */}
          {!loading && featuredProducts.length > 0 && (
            <div className="sm:hidden text-center mt-8">
              <Link 
                to={`/products${selectedCategory ? `?category=${selectedCategory}` : ''}`} 
                className="inline-block bg-black text-white px-6 py-3 font-medium hover:bg-gray-800 transition-colors"
              >
                មើលទំនិញ{selectedCategory ? ' ក្នុងប្រភេទនេះ' : 'ទាំងអស់'}
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;